/**
 * Bonsai Mail - Outlook Auth Callback
 * 
 * This page is loaded inside the Office dialog after Microsoft OAuth completes.
 * It sends the auth result back to the parent task pane and closes.
 */

import { useEffect, useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function BonsaiOutlookCallback() {
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState("");

  useEffect(() => {
    
    // Parse URL params
    const urlParams = new URLSearchParams(window.location.search);
    const authSuccess = urlParams.get("auth");
    const bonsaiUserId = urlParams.get("bonsai_user_id");
    const errorParam = urlParams.get("error");
    
    if (errorParam) {
      console.error("[BonsaiOutlookCallback] Auth error:", errorParam);
      setStatus("error");
      setError(errorParam);
      return;
    }
    
    if (authSuccess === "success" && bonsaiUserId) {
      setStatus("success");
      
      // Store in sessionStorage for the parent window
      sessionStorage.setItem("bonsai_user_id", bonsaiUserId);
      
      // Check if we're in an Office dialog
      if (typeof Office !== "undefined" && Office.context && Office.context.ui) {
        // Send message to parent and close
        Office.context.ui.messageParent(JSON.stringify({
          status: "success",
          bonsai_user_id: bonsaiUserId
        }));
      } else {
        // Not in Office dialog - redirect to SPA preview.
        // BM-H P0-1 (01/09/2026): route renamed to /bonsai/outlook-preview to
        // make clear this is not the shipped add-in (see App.js:497 comment).
        setTimeout(() => {
          window.location.href = "/bonsai/outlook-preview";
        }, 1500);
      }
    } else {
      console.error("[BonsaiOutlookCallback] Missing auth params");
      setStatus("error");
      setError("Authentication failed - missing parameters");
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-hover/60 p-4">
      <Card className="max-w-sm w-full">
        <CardContent className="p-6 text-center">
          {status === "processing" && (
            <>
              <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
              <h2 className="text-lg font-semibold text-white mb-2">
                Completing sign-in...
              </h2>
              <p className="text-sm text-slate-500">
                Please wait while we complete your authentication.
              </p>
            </>
          )}
          
          {status === "success" && (
            <>
              <CheckCircle2 className="w-12 h-12 text-status-green mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-status-green mb-2">
                Signed in successfully!
              </h2>
              <p className="text-sm text-slate-500">
                This window will close automatically.
              </p>
            </>
          )}
          
          {status === "error" && (
            <>
              <XCircle className="w-12 h-12 text-status-red mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-status-red mb-2">
                Sign-in failed
              </h2>
              <p className="text-sm text-slate-500">
                {error || "An error occurred during authentication."}
              </p>
              <p className="text-xs text-slate-400 mt-4">
                Please close this window and try again.
              </p>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Load Office.js for dialog communication */}
      <script src="https://appsforoffice.microsoft.com/lib/1/hosted/office.js" type="text/javascript"></script>
    </div>
  );
}
